import React from 'react';

import { Icon } from '@core/js/components/common/Icon';

import { useTranslations } from '@core/js/hooks/i18n';
import { clsb } from '@core/js/utils/classNames.util';

import { useTimer } from './hooks/useTimer';
import { TTimerProps } from './types';

import style from './Timer.scss';

export const Timer: React.FC<TTimerProps> = ({
    label,
    typeIconLabel = 'lb_clockb',
    countTo,
    addingZeros,
    classNames,
    tKey = 'playtech.mobile.system.timer.short.',
    separator = ':',
    separatorAfterDay,
    isCheckDays,
    isCheckHours,
    handlerEndTime,
    isDaysLabel = true,
    isTimeLabel = true
}) => {
    const t = useTranslations(tKey);
    const buildCls = clsb([style, classNames]);
    const {
        days,
        hours,
        min,
        sec
    } = useTimer({ countTo, addingZeros, handlerEndTime });

    const renderLabel = () => {
        if (!label) {
            return null;
        }

        return (
            <div className={buildCls(['labelContainer'])}>
                <Icon type={typeIconLabel} className={buildCls(['labelIcon'])} />

                <span className={buildCls(['labelText'])}>{label}&nbsp;</span>
            </div>
        );
    };

    if (!countTo) {
        return (
            <div
                data-testid="Timer_container"
                className={buildCls(['container'])}
            >
                {renderLabel()}
            </div>
        );
    }

    return (
        <div
            data-testid="Timer_container"
            className={buildCls(['container'])}
        >
            {renderLabel()}

            <div className={buildCls(['dateContainer'])}>
                {isCheckDays && !Number(days)
                    ? null
                    : (
                        <>
                            <div className={buildCls(['item', 'itemDays', days.length > 2 ? 'isBiggerDayField' : ''])}>
                                <span
                                    data-testid="Timer_days"
                                    className={buildCls(['value'])}
                                >
                                    {days}
                                </span>

                                {isDaysLabel && (
                                    <span
                                        data-testid="Timer_daysLabel"
                                        className={buildCls(['label'])}
                                    >
                                        {t('days')}
                                    </span>
                                )}
                            </div>

                            <div className={buildCls(['separator', 'separator0'])}>{separatorAfterDay || separator}</div>
                        </>
                    )
                }

                {isCheckHours && !Number(hours)
                    ? null
                    : (
                        <>
                            <div className={buildCls(['item', 'itemHours'])}>
                                <span
                                    data-testid="Timer_hours"
                                    className={buildCls(['value'])}
                                >
                                    {hours}
                                </span>

                                {isTimeLabel && (
                                    <span
                                        data-testid="Timer_hoursLabel"
                                        className={buildCls(['label'])}
                                    >
                                        {t('hours')}
                                    </span>
                                )}
                            </div>

                            <div className={buildCls(['separator', 'separator1'])}>{separator}</div>
                        </>
                    )
                }

                <div className={buildCls(['item', 'itemMin'])}>
                    <span
                        data-testid="Timer_min"
                        className={buildCls(['value'])}
                    >
                        {min}
                    </span>

                    {isTimeLabel && (
                        <span
                            data-testid="Timer_minutesLabel"
                            className={buildCls(['label'])}
                        >
                            {t('minutes')}
                        </span>
                    )}
                </div>

                <div className={buildCls(['separator', 'separator2'])}>{separator}</div>

                <div className={buildCls(['item', 'itemSec'])}>
                    <span
                        data-testid="Timer_seconds"
                        className={buildCls(['value'])}
                    >
                        {sec}
                    </span>

                    {isTimeLabel && (
                        <span
                            data-testid="Timer_secondsLabel"
                            className={buildCls(['label'])}
                        >
                            {t('seconds')}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};
